// Accueil du support technique (P03) : état des services en cartes, sans aucune donnée métier. Les
// connecteurs non raccordés sont signalés en tête, avec leur effet ; le raccordement relève de la DGH.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure, heure } from '../../composants/format';

interface EtatServices {
  version: string;
  demarreLe: string;
  base: { disponible: boolean; latenceMs: number; version: string | null; migrationsAppliquees: number | null };
  stockageDocuments: { chemin: string; chiffrement: string };
  connecteurs: { nom: string; etat: string }[];
}

/** Effet concret d'un connecteur absent, pour qu'on sache ce qui ne fonctionne pas. */
const EFFETS_CONNECTEURS: Record<string, string> = {
  'Courriel (SMTP)': 'Les notifications par courriel ne partent pas',
  'SMS (adaptateur HTTP)': 'Les alertes par SMS ne partent pas',
  'Fond de carte externe': 'La carte utilise le tracé interne des provinces',
};

export function AccueilTechnique() {
  const etat = useQuery({ queryKey: ['exploitation'], queryFn: async () => (await api<EtatServices>('/api/v1/exploitation/etat')).donnees, refetchInterval: 30_000 });
  if (etat.isLoading) return <Squelette lignes={5} />;
  if (etat.error) return <MessageErreur erreur={etat.error} />;
  const e = etat.data!;
  const nonRaccordes = e.connecteurs.filter((c) => c.etat !== 'actif');
  return (
    <div className="accueil-technique">
      <div className="page-tete">
        <div>
          <h1>Exploitation technique</h1>
          <p>Profil sans accès aux données métier, aux documents ni aux messages.</p>
        </div>
        <button type="button" className="bouton primaire" disabled={etat.isFetching} onClick={() => void etat.refetch()}>
          {etat.isFetching ? 'Vérification en cours' : 'Vérifier maintenant'}
        </button>
      </div>

      <section className={e.base.disponible ? 'a-faire' : 'a-faire rouge'} aria-labelledby="titre-synthese">
        <Icone nom="incident" taille={22} />
        <div>
          <h2 id="titre-synthese">
            {e.base.disponible ? 'Les services essentiels fonctionnent.' : 'La base de données ne répond pas.'}
            {nonRaccordes.length > 0 && ` ${nonRaccordes.length} connecteur${nonRaccordes.length > 1 ? 's ne sont' : ' n’est'} pas raccordé${nonRaccordes.length > 1 ? 's' : ''}.`}
          </h2>
        </div>
        <span className="attenue">Dernière vérification à {heure(etat.dataUpdatedAt)}</span>
      </section>

      <div className="grille-2">
        <section className="carte-bloc" aria-labelledby="titre-services">
          <h2 id="titre-services">État des services</h2>
          <dl className="etats-services">
            <div>
              <dt>Version de l’application</dt>
              <dd>{e.version}</dd>
            </div>
            <div>
              <dt>Démarrage du serveur</dt>
              <dd>{dateHeure(e.demarreLe)}</dd>
            </div>
            <div>
              <dt>Base de données</dt>
              <dd>
                <span className={`point-etat ${e.base.disponible ? 'vert' : 'rouge'}`}>
                  {e.base.disponible ? 'Disponible' : 'Indisponible'}, latence {e.base.latenceMs} ms
                </span>
              </dd>
            </div>
            <div>
              <dt>Version PostgreSQL</dt>
              <dd>{e.base.version ?? <span className="indisponible">indisponible</span>}</dd>
            </div>
            <div>
              <dt>Migrations appliquées</dt>
              <dd>{e.base.migrationsAppliquees ?? <span className="indisponible">indisponible</span>}</dd>
            </div>
            <div>
              <dt>Stockage des documents</dt>
              <dd>
                <span className="point-etat vert">Chiffré, {e.stockageDocuments.chiffrement}</span>
              </dd>
            </div>
          </dl>
        </section>
        <section className="carte-bloc" aria-labelledby="titre-connecteurs">
          <h2 id="titre-connecteurs">Connecteurs</h2>
          <ul className="liste-connecteurs">
            {e.connecteurs.map((c) => (
              <li key={c.nom} className={c.etat === 'actif' ? undefined : 'non-raccorde'}>
                <span>
                  <b>{c.nom}</b>
                  {c.etat !== 'actif' && EFFETS_CONNECTEURS[c.nom] && <small>{EFFETS_CONNECTEURS[c.nom]}</small>}
                </span>
                <span className="etat-connecteur">{c.etat}</span>
              </li>
            ))}
          </ul>
          <p className="attenue">Le raccordement relève de l’administration fonctionnelle (module des connecteurs au lot 6) ; le support peut seulement le constater.</p>
        </section>
      </div>
    </div>
  );
}
